"use client";

import {
  Trophy,
  TrendingUp,
  Activity,
  Star,
  ArrowRight,
  Zap,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Flag } from "@/components/shared/flags";
import { Badge } from "@/components/ui/badge";
import { useState } from "react";

interface HeroCountdownProps {
  aktivnehlasovanie_id?: string | null;
}

const HERO_SECTION_CLASSES =
  "relative overflow-hidden bg-background pt-12 pb-12 sm:pb-20 lg:pt-20 lg:pb-32";
const HERO_GRADIENT_CLASSES = "absolute inset-0 -z-10 opacity-50";
const FEATURE_GRID_CLASSES =
  "grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 sm:mt-32";
const FEATURE_CARD_CLASSES = cn(
  "flex flex-col items-center text-center gap-6 p-8 rounded-3xl bg-background/40 border border-muted/20 shadow-lg",
  "hover:shadow-2xl transition-all duration-500 hover:bg-background/60 group backdrop-blur-sm",
);

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function HeroCountdown(props: HeroCountdownProps = {}) {
  const [currentSlide, setCurrentSlide] = useState(0);

  const matches = [
    {
      id: 1,
      date: "14. februára 2026",
      homeTeam: { code: "SVK", name: "Slovensko" },
      awayTeam: { code: "SWE", name: "Švédsko" },
      score: { home: 3, away: 5 },
      status: "Koniec",
      statusType: "warning",
      headline: "Boj o postup pokračuje",
      highlight: "stále máme šancu!",
      description:
        "Napri prehre so Švédskom 3:5 má Slovensko vďaka výhre Fínska stále šancu na priamy postup do štvrťfinále. Sledujte postupové scenáre!",
      scorers: [
        { meno: "Slafkovský", body: "1+1", detail: "1G 1A" },
        { meno: "Dvorský", body: "1+0", detail: "1G" },
        { meno: "Gernát", body: "1+2", detail: "1G 2A" },
      ],
      articleLink:
        "/clanky/slovensko-prehralo-so-svedskom-3-5-scenar-postupu-zoh-2026",
    },
    {
      id: 2,
      date: "11. februára 2026",
      homeTeam: { code: "SVK", name: "Slovensko" },
      awayTeam: { code: "FIN", name: "Fínsko" },
      score: { home: 4, away: 1 },
      status: "Koniec",
      statusType: "success",
      headline: "Slafkovský s bodmi 2+1",
      highlight: "režíroval triumf!",
      description:
        "Slovensko vstúpilo do olympijského turnaja víťazne. Juraj Slafkovský strelil dva góly a pridal asistenciu (2+1), Dvorský a Ružička pridali ďalšie. Brankár predviedol 39 zákrokov!",
      scorers: [
        { meno: "Slafkovský", body: "2+1", detail: "2G 1A" },
        { meno: "Dvorský", body: "1+1", detail: "1G 1A" },
        { meno: "Ružička", body: "1+0", detail: "1G" },
      ],
      articleLink:
        "/clanky/slovensko-finsko-4-1-zoh-2026-slafkovsky-dvojgolovy-zazrak",
    },
  ];

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % matches.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + matches.length) % matches.length);
  };

  const currentMatch = matches[currentSlide];
  return (
    <section className={HERO_SECTION_CLASSES}>
      <div className={HERO_GRADIENT_CLASSES}>
        <div className="absolute inset-0 bg-[radial-gradient(45%_45%_at_50%_50%,hsl(var(--primary)/0.15)_0%,transparent_100%)]" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center">
          {/* Live badge */}
          <Badge
            variant="outline"
            className={cn(
              "inline-flex items-center gap-2 rounded-full bg-status-success/10",
              "px-6 py-2 text-xs font-black text-status-success mb-6 uppercase tracking-[0.2em]",
              "border border-status-success/30 shadow-sm animate-pulse",
            )}
          >
            <Zap className="h-4 w-4" />
            ZOH 2026 • TURNAJ ZAČAL
          </Badge>

          {/* Hlavný výsledok */}
          <div className="mb-8">
            <p className="text-sm font-black text-muted-foreground uppercase tracking-[0.2em] mb-6">
              Skupina B • {currentMatch.date}
            </p>

            <div className="flex items-center justify-center gap-4 sm:gap-8 md:gap-12">
              {/* SVK */}
              <div className="flex flex-col items-center gap-3">
                <Flag
                  kod={currentMatch.homeTeam.code}
                  size={64}
                  className="shadow-lg rounded-lg border-2 border-primary/30"
                />
                <span className="text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tighter text-primary">
                  {currentMatch.homeTeam.code}
                </span>
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                  {currentMatch.homeTeam.name}
                </span>
              </div>

              {/* Skóre */}
              <div className="flex flex-col items-center gap-2">
                <div className="text-6xl sm:text-8xl md:text-9xl font-black tracking-tighter tabular-nums leading-none">
                  <span className="text-primary drop-shadow-[0_0_20px_rgba(var(--primary-rgb),0.4)]">
                    {currentMatch.score.home}
                  </span>
                  <span className="text-muted-foreground/30 mx-2 sm:mx-4">
                    :
                  </span>
                  <span className="text-foreground/60">
                    {currentMatch.score.away}
                  </span>
                </div>
                <Badge
                  variant="outline"
                  className={cn(
                    "rounded-full px-5 py-1.5 text-[10px] font-black uppercase tracking-widest",
                    currentMatch.statusType === "success"
                      ? "bg-status-success/10 text-status-success border-status-success/20"
                      : "bg-status-warning/10 text-status-warning border-status-warning/20",
                  )}
                >
                  {currentMatch.status}
                </Badge>
              </div>

              {/* Away team */}
              <div className="flex flex-col items-center gap-3">
                <Flag
                  kod={currentMatch.awayTeam.code}
                  size={64}
                  className="shadow-lg rounded-lg border-2 border-muted/30"
                />
                <span className="text-2xl sm:text-3xl md:text-4xl font-black uppercase tracking-tighter text-muted-foreground">
                  {currentMatch.awayTeam.code}
                </span>
                <span className="text-xs font-bold text-muted-foreground uppercase tracking-widest">
                  {currentMatch.awayTeam.name}
                </span>
              </div>
            </div>
          </div>

          {/* Headline */}
          <h1
            className={cn(
              "text-3xl sm:text-5xl lg:text-7xl font-black tracking-tighter text-foreground mb-6 max-w-5xl uppercase",
              "drop-shadow-sm leading-[0.95]",
            )}
          >
            {currentMatch.headline}{" "}
            <span className="text-primary drop-shadow-[0_0_15px_rgba(var(--primary-rgb),0.3)]">
              {currentMatch.highlight}
            </span>
          </h1>

          <p
            className={cn(
              "text-lg text-muted-foreground/80 mb-10 max-w-2xl leading-relaxed font-medium",
              "sm:text-xl",
            )}
          >
            {currentMatch.description}
          </p>

          {/* Strelci */}
          <div className="flex flex-wrap items-center justify-center gap-3 mb-10">
            {currentMatch.scorers.map((strelec) => (
              <div
                key={strelec.meno}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-primary/5 border border-primary/10 text-sm"
              >
                <Trophy className="h-3.5 w-3.5 text-primary" />
                <span className="font-black text-foreground">
                  {strelec.meno}
                </span>
                <span className="text-primary font-black">{strelec.body}</span>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="flex flex-col sm:flex-row gap-6">
            <Button
              asChild
              size="lg"
              className="px-10 font-black h-14 rounded-full shadow-xl hover:shadow-2xl transition-all bg-primary text-primary-foreground hover:scale-105 active:scale-95 uppercase tracking-widest text-xs"
            >
              <Link
                href={currentMatch.articleLink}
                aria-label="Čítať článok o zápase"
              >
                Čítať článok
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="px-10 font-black h-14 rounded-full border-muted/20 bg-background/50 backdrop-blur-sm hover:bg-muted/30 transition-all hover:scale-105 active:scale-95 uppercase tracking-widest text-xs"
            >
              <Link href="/skupiny" aria-label="Pozrieť tabuľky skupín">
                Tabuľky skupín
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="lg"
              className="px-10 font-black h-14 rounded-full border-muted/20 bg-background/50 backdrop-blur-sm hover:bg-muted/30 transition-all hover:scale-105 active:scale-95 uppercase tracking-widest text-xs"
            >
              <Link href="/slovensko" aria-label="Naša reprezentácia">
                Naša Reprezentácia
              </Link>
            </Button>
          </div>

          {/* Slider controls */}
          <div className="flex items-center justify-center gap-4 mt-8">
            <Button
              variant="outline"
              size="sm"
              onClick={prevSlide}
              className="rounded-full w-10 h-10 p-0 border-muted/20 bg-background/50 backdrop-blur-sm hover:bg-muted/30"
              aria-label="Predchádzajúci zápas"
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>

            <div className="flex gap-2">
              {matches.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentSlide(index)}
                  className={cn(
                    "w-2 h-2 rounded-full transition-all duration-300",
                    index === currentSlide
                      ? "bg-primary w-8"
                      : "bg-muted/30 hover:bg-muted/50",
                  )}
                  aria-label={`Prejsť na zápas ${index + 1}`}
                />
              ))}
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={nextSlide}
              className="rounded-full w-10 h-10 p-0 border-muted/20 bg-background/50 backdrop-blur-sm hover:bg-muted/30"
              aria-label="Ďalší zápas"
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <div className={FEATURE_GRID_CLASSES}>
          {[
            {
              icon: TrendingUp,
              title: "Forma hráčov",
              desc: "Sledujte góly, body a asistencie v reálnom čase z klubových súťaží.",
            },
            {
              icon: Activity,
              title: "Analýza zostavy",
              desc: "Vypočítame vám priemerný vek, hodnotu a štatistiky vašej zostavy.",
            },
            {
              icon: Star,
              title: "Top výbery",
              desc: "Pozrite sa, koho do Milána posiela hokejová komunita.",
            },
          ].map((item) => (
            <div key={item.title} className={FEATURE_CARD_CLASSES}>
              <div className="p-4 rounded-2xl bg-primary/10 text-primary shrink-0 group-hover:scale-110 group-hover:rotate-3 transition-transform duration-500 shadow-inner">
                <item.icon className="h-8 w-8" />
              </div>
              <div className="space-y-3">
                <h2 className="font-black text-xl text-foreground uppercase tracking-tight">
                  {item.title}
                </h2>
                <p className="text-sm text-muted-foreground font-medium leading-relaxed">
                  {item.desc}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
